import { MapPin } from "lucide-react";
import { Basemap } from "components/basemap/basemap";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import * as QueryKeys from "data";
import { fetchAuthJWT, fetchMapsLayerJson } from "data/queries-common";
import { requestTransformer } from "pages/geoscape-data/explorer/explorer-page";
import { TileJSON } from "pages/geoscape-data/explorer/layers/models";
import { ZoomControl } from "pages/geoscape-data/explorer/zoom-control";
import { useEffect, useRef, useState } from "react";
import {
  CircleLayerSpecification,
  Layer,
  LineLayerSpecification,
  MapRef,
  Marker,
  Popup,
  Source,
} from "react-map-gl/maplibre";
import { useQuery } from "@tanstack/react-query";
import { PredictiveAddressManager } from "./predictive-address-manager";

export const VisualiseMap = (predictiveManager: PredictiveAddressManager) => {
  const mapRef = useRef<MapRef>();
  const initialZoom: number = 17;
  const addressZoom: number = 17;

  const [popupInfo, setPopupInfo] = useState(false);
  const [dynamicZoom, setDynamicZoom] = useState(initialZoom);

  const gnafLayer: CircleLayerSpecification = {
    id: "gnaf",
    type: "circle",
    source: "gnaf",
    "source-layer": "gnaf",
    paint: {
      "circle-color": "red",
      "circle-radius": 3,
    },
  };

  const cadastreLayer: LineLayerSpecification = {
    id: "cadastre",
    type: "line",
    source: "cadastre",
    "source-layer": "cadastre",
    paint: {
      "line-color": "purple",
      "line-width": 2,
    },
  };

  useEffect(() => {
    if (predictiveManager.address?.address.geometry) {
      mapRef.current?.flyTo({
        center: [
          predictiveManager.address.address.geometry.coordinates[0],
          predictiveManager.address.address.geometry.coordinates[1],
        ],
        duration: 2000,
        zoom: addressZoom,
        offset: [0, 0],
      });
    }
  }, [predictiveManager.address?.address.geometry]);

  const { enqueueQueryFailed } = useCustomSnackbars();

  const jwt = useQuery({
    queryKey: [QueryKeys.jwt],
    queryFn: () => fetchAuthJWT(),
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (jwt.isError) {
      enqueueQueryFailed(jwt.error.toString());
    }
  }, [jwt.isError]);

  const addAuthToTileUrl = (tileUrl: string[]) => {
    return tileUrl.map((x) => `${x}?key=${jwt.data}`);
  };

  const cadastreLayerJsonQuery = useQuery({
    queryKey: [QueryKeys.mapsLayerJson, "cadastre"],
    queryFn: () =>
      fetchMapsLayerJson(jwt.isSuccess ? jwt.data : "", "cadastre"),
    enabled: !!jwt.data,
    retry: true,
  });

  useEffect(() => {
    if (cadastreLayerJsonQuery.isSuccess && cadastreLayerJsonQuery.data) {
      setCadastreLayerJson(cadastreLayerJsonQuery.data);
    }
  }, [cadastreLayerJsonQuery.isSuccess]);

  const gnafLayerJsonQuery = useQuery({
    queryKey: [QueryKeys.mapsLayerJson, "gnaf"],
    queryFn: () => fetchMapsLayerJson(jwt.isSuccess ? jwt.data : "", "gnaf"),
    enabled: !!jwt.data,
    retry: true,
  });

  useEffect(() => {
    if (gnafLayerJsonQuery.isSuccess && gnafLayerJsonQuery.data) {
      setGnafLayerJson(gnafLayerJsonQuery.data);
    }
  }, [gnafLayerJsonQuery.isSuccess]);

  const [gnafLayerJson, setGnafLayerJson] = useState<TileJSON | null>(null);
  const [cadastreLayerJson, setCadastreLayerJson] = useState<TileJSON | null>(
    null,
  );

  const resetCompass = () => {
    if (mapRef.current === undefined) return;
    mapRef.current.easeTo({
      bearing: 0,
      pitch: 0,
    });
  };

  const handleZoom = (zoom: number): void => {
    setDynamicZoom(zoom);
    mapRef.current?.flyTo({ zoom: zoom, duration: 500 });
  };

  return (
    <div className="relative w-full flex-1 min-h-87.5 rounded-lg overflow-hidden border">
      {jwt.isSuccess && (
        <Basemap
          // @ts-ignore
          mapRef={mapRef}
          latitude={
            predictiveManager.address?.address.geometry
              ? predictiveManager.address.address.geometry.coordinates[0]
              : -35.32201813
          }
          longitude={
            predictiveManager.address?.address.geometry
              ? predictiveManager.address.address.geometry.coordinates[1]
              : 149.14668681
          }
          zoom={initialZoom}
          attributionControl={false}
          style={{ width: "100%", height: "100%" }}
          onZoom={() => {}}
          transformRequest={requestTransformer}
        >
          {predictiveManager.address?.address.geometry && (
            <Marker
              key={"selected-address"}
              longitude={
                predictiveManager.address.address.geometry.coordinates[0]
              }
              latitude={
                predictiveManager.address.address.geometry.coordinates[1]
              }
              onClick={() => setPopupInfo(true)}
              style={{ position: "absolute", left: 0, top: 0 }}
            >
              <MapPin className="size-6 text-destructive fill-destructive" />
            </Marker>
          )}
          {predictiveManager.address?.address.geometry &&
            popupInfo &&
            predictiveManager.address.address.properties.formatted_address && (
              <Popup
                anchor="top"
                longitude={
                  predictiveManager.address.address.geometry.coordinates[0]
                }
                latitude={
                  predictiveManager.address.address.geometry.coordinates[1]
                }
                onClose={() => setPopupInfo(false)}
              >
                <p className="text-sm text-black">
                  {
                    predictiveManager.address.address.properties
                      .formatted_address
                  }
                </p>
              </Popup>
            )}
          {gnafLayerJson && (
            <Source
              key="gnaf"
              id="gnaf"
              type="vector"
              maxzoom={15}
              tiles={addAuthToTileUrl(gnafLayerJson.tiles)}
            >
              <Layer {...gnafLayer} />
            </Source>
          )}
          {cadastreLayerJson && (
            <Source
              key="cadastre"
              id="cadastre"
              type="vector"
              maxzoom={16}
              tiles={addAuthToTileUrl(cadastreLayerJson.tiles)}
            >
              <Layer {...cadastreLayer} />
            </Source>
          )}
          <div className="absolute right-4 top-4 flex flex-col gap-2">
            <ZoomControl
              resetCompass={resetCompass}
              dynamicZoom={dynamicZoom}
              onZoom={handleZoom}
            />
          </div>
        </Basemap>
      )}
    </div>
  );
};
